import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ArrowLeft, User, BookOpen, Sparkles, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { blogs } from "../mock/mock";
import CTA from "../components/sections/CTA";
import { KeyTakeawaysCard, BlogMetaInfographic } from "../components/infographics/BlogInfographics";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const BlogPage = () => {
  const { id } = useParams();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => ["All", ...Array.from(new Set(blogs.map((b) => b.category)))], []);

  const filtered = useMemo(() => {
    return blogs.filter((b) => {
      const q = query.trim().toLowerCase();
      const matchQ = !q || b.title.toLowerCase().includes(q) || b.excerpt.toLowerCase().includes(q);
      const matchC = activeCategory === "All" || b.category === activeCategory;
      return matchQ && matchC;
    });
  }, [query, activeCategory]);

  // Single blog view
  if (id) {
    const post = blogs.find((b) => b.id === id);
    if (!post) {
      return (
        <div className="py-32 text-center">
          <h2 className="font-display text-3xl font-bold text-stone-800 mb-4">Article not found</h2>
          <Link to="/blog">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full">Back to Blog</Button>
          </Link>
        </div>
      );
    }
    const related = blogs.filter((b) => b.id !== post.id && b.category === post.category).slice(0, 3);
    return (
      <div>
        <section className="pt-12 pb-8 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-orange-700 hover:text-orange-800 font-semibold text-sm mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <span className="inline-block bg-orange-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4">
              {post.category}
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-stone-800 leading-tight mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-sm text-stone-600 mb-2">
              <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-orange-600" /> {post.author}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-orange-600" /> {formatDate(post.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-600" /> {post.readTime}</span>
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <img src={post.image} alt={post.title} className="w-full h-72 md:h-96 object-cover rounded-3xl shadow-xl mb-10" />
            <BlogMetaInfographic post={post} />
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-stone-700 leading-relaxed font-medium mb-6 border-l-4 border-orange-500 pl-5 italic">
                {post.excerpt}
              </p>

              {/* Structured sections */}
              {post.sections && post.sections.length > 0 ? (
                <div className="space-y-4">
                  {post.sections.map((sec, i) => {
                    const secKey = `${sec.type}-${i}`;
                    if (sec.type === "heading") {
                      return (
                        <h2 key={secKey} className="font-display text-2xl md:text-3xl font-bold text-orange-800 mt-10 mb-3 border-b border-orange-200 pb-2">
                          {sec.text}
                        </h2>
                      );
                    }
                    if (sec.type === "subheading") {
                      return (
                        <h3 key={secKey} className="font-display text-xl font-bold text-stone-800 mt-6 mb-2">
                          {sec.text}
                        </h3>
                      );
                    }
                    if (sec.type === "bullets") {
                      return (
                        <ul key={secKey} className="space-y-2 my-4">
                          {sec.items.map((it) => (
                            <li key={it} className="flex items-start gap-3 text-stone-700 leading-relaxed">
                              <div className="w-2 h-2 rounded-full bg-orange-500 mt-2.5 flex-shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (sec.type === "image") {
                      return (
                        <figure key={secKey} className="my-8">
                          <img src={sec.src} alt={sec.caption || "Blog image"} className="w-full rounded-2xl shadow-lg" />
                          {sec.caption && (
                            <figcaption className="text-center text-sm text-stone-500 italic mt-3">
                              {sec.caption}
                            </figcaption>
                          )}
                        </figure>
                      );
                    }
                    return (
                      <p key={secKey} className="text-stone-700 leading-relaxed text-lg">
                        {sec.text}
                      </p>
                    );
                  })}
                </div>
              ) : (
                <>
                  <p className="text-stone-700 leading-relaxed text-lg whitespace-pre-line">{post.content}</p>
                  <p className="text-stone-700 leading-relaxed text-lg mt-6">
                    At Aadi Gayanam, our mission is simple — to raise children who are rooted in Sanatan values, ready for the modern world and joyful in their journey. Every day, we design experiences that build the head, heart, hands and habits of every child.
                  </p>
                  <p className="text-stone-700 leading-relaxed text-lg mt-4">
                    If this article resonated with you and you'd like to know more about our approach or admissions for the 2027-28 academic year, we'd love to hear from you.
                  </p>
                </>
              )}
            </div>

            <KeyTakeawaysCard post={post} />

            {/* Closing Sanskrit quote block if provided */}
            {post.sanskritQuote && (
              <div className="mt-12 p-8 md:p-10 bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 text-white rounded-3xl shadow-xl text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <div className="absolute top-2 left-2 w-40 h-40 border-2 border-yellow-300 rounded-full" />
                  <div className="absolute bottom-2 right-2 w-52 h-52 border-2 border-yellow-300 rounded-full" />
                </div>
                <div className="relative z-10">
                  <p className="font-devanagari text-xl md:text-2xl text-yellow-100 leading-relaxed whitespace-pre-line mb-4">
                    {post.sanskritQuote.sanskrit}
                  </p>
                  <p className="text-orange-100 italic max-w-2xl mx-auto text-sm md:text-base">
                    {post.sanskritQuote.meaning}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 p-8 bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl border border-orange-100 text-center">
              <p className="font-devanagari text-2xl text-orange-700 mb-2">आदि गायनम् परिवार में शामिल हों</p>
              <h3 className="font-display text-2xl font-bold text-stone-800 mb-2">Join the Aadi Gayanam Family</h3>
              <p className="text-stone-600 text-sm mb-4">Give your child the gift of a strong beginning.</p>
              <Link to="/admission">
                <Button className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full px-8 h-11 btn-shine">
                  Enquire About Admissions <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-16 pattern-warm">
            <div className="container mx-auto px-4 max-w-6xl">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-stone-800 mb-8">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((b) => (
                  <Link key={b.id} to={`/blog/${b.id}`} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all">
                    <img src={b.image} alt={b.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform" />
                    <div className="p-5">
                      <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">{b.category}</span>
                      <h4 className="font-display font-bold text-stone-800 mt-1 group-hover:text-orange-700 line-clamp-2">{b.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    );
  }

  // Blog listing view
  return (
    <div>
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Aadi Gayanam Blog</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Stories, Tips & <span className="text-orange-600">Wisdom</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">Curated articles on early childhood, Sanatan values, yoga, parenting and modern learning.</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          {/* Search + categories */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-10">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search articles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 h-11 bg-orange-50 border-orange-200 focus-visible:ring-orange-500"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    activeCategory === c
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-stone-700 border-stone-200 hover:border-orange-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-12 h-12 text-orange-300 mx-auto mb-3" />
              <p className="text-stone-500">No articles match your search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((b) => (
                <Link
                  key={b.id}
                  to={`/blog/${b.id}`}
                  className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-orange-100 flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-orange-700 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow">
                      {b.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-stone-500 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(b.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.readTime}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-stone-800 mb-2 line-clamp-2 group-hover:text-orange-700 transition-colors">
                      {b.title}
                    </h3>
                    <p className="text-sm text-stone-600 mb-4 line-clamp-3 flex-1">{b.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-orange-700 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default BlogPage;
